'use client';
import { useEffect } from 'react';

type NewRelicClientProps = {
  appId: string;
};

/**
 * Sets a custom attribute in New Relic for tracking across events.
 * This can be used to add dimensions like experiment IDs and variants to New Relic reports.
 *
 * Includes a retry mechanism to handle race conditions where New Relic may not be
 * fully initialized when this function is called.
 *
 * @param name - The name of the custom attribute
 * @param value - The value of the custom attribute (string, number, boolean, or null)
 * @param persist - Optional. If true, the attribute will persist across page loads within the same session
 */
export function setNewRelicCustomAttribute(
  name: string,
  value: string | number | boolean | null,
  persist: boolean = false
): void {
  const maxRetries = 10;
  const attemptSet = (retriesLeft: number = maxRetries, delay: number = 100): void => {
    if ((globalThis as any).newrelic) {
      (globalThis as any).newrelic.setCustomAttribute(name, value, persist);
      return;
    }

    // New Relic not ready yet, retry if we have attempts left
    if (retriesLeft > 0) {
      setTimeout(() => attemptSet(retriesLeft - 1, delay * 2), delay);
    }
  };

  attemptSet();
}

export default function NewRelicClient({ appId }: NewRelicClientProps) {
  useEffect(() => {
    if (!appId) return;

    const options = {
      init: {
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ['bam.eu01.nr-data.net'] },
        session_trace: { enabled: false },
        page_view_timing: { enabled: true },
        distributed_tracing: { enabled: false },
      },
      loader_config: {
        accountID: '4594021',
        trustKey: '4594021',
        agentID: appId,
        licenseKey: 'NRJS-3c9aacc63b429f8d412',
        applicationID: appId,
      },
      info: {
        beacon: 'bam.eu01.nr-data.net',
        errorBeacon: 'bam.eu01.nr-data.net',
        licenseKey: 'NRJS-3c9aacc63b429f8d412',
        applicationID: appId,
        sa: 1,
      },
    };

    // Initialize New Relic Browser Agent
    // Dynamic import is required because the New Relic agent accesses browser globals
    // during module evaluation, which would fail during SSR/build even with 'use client'
    import('@newrelic/browser-agent/loaders/browser-agent')
      .then(({ BrowserAgent }) => {
        try {
          const _agent = new BrowserAgent(options);

          if ((globalThis as any).newrelic) {
            (globalThis as any).newrelic.addRelease('engage', 'static');
          }
        } catch (error) {
          console.error('Failed to initialize New Relic Browser Agent', error);
        }
      })
      .catch((error) => {
        console.error('Failed to load New Relic Browser Agent module', error);
      });
  }, [appId]);

  return null;
}
